"use client";

import dynamic from "next/dynamic";
import { JSONContent } from "novel";
import { useMemo } from "react";
import AnalysisDate, {
  AnalysisDateProps,
} from "~/src/components/feature/analysis/info/AnalysisDate";
import Creator from "~/src/components/feature/analysis/info/Creator";
import TagList from "~/src/components/feature/analysis/info/TagList";
import WorkListInDetail from "~/src/components/feature/analysis/info/WorkListInDetail";
import { convertAnalysisToRichText } from "~/src/lib/analysis/convertToRichText";
import { AnalysisDocument } from "~/src/type/firebase/firestore";
import { GraphInfo } from "~/src/type/ui/article/article";
import AffiliateLink from "./AffiliateLink";
import Favorit from "./Favorit";

const NovelPreview = dynamic(() => import("./NovelPreview"), { ssr: false });

type AnalysisDetailsProps = {
  analysisId: string;
  contents: AnalysisDocument;
  graph: GraphInfo[];
  analysisTime: AnalysisDateProps;
  liked: boolean;
  localFavoritCount?: boolean;
};

/**
 * 分析記事詳細ページ
 */
export default function AnalysisDetails({
  analysisId,
  contents,
  graph,
  analysisTime,
  liked,
}: AnalysisDetailsProps) {
  const richTextContent = useMemo(
    () =>
      contents.body ? (contents.body as JSONContent) : convertAnalysisToRichText(contents, graph),
    [contents, graph],
  );

  return (
    <section className="flex flex-col gap-10 bg-WHITE_2 pb-10 md:px-4">
      <div className="rounded-md bg-WHITE_1 p-6 md:p-10 md:py-12">
        {/* 見出し情報 */}
        <header>
          <AnalysisDate
            create={analysisTime.create}
            update={analysisTime.update}
          />
          <h2 className="max-w-3xl text-xl font-bold text-BLACK_1 md:text-2xl/8">
            {contents.title}
          </h2>
          <Creator
            // url={"https://i.imgur.com/yXOvdOSs.jpg"}
            name={contents.userName}
          />
          <TagList
            tag={contents.tag}
            background={true}
          />
        </header>

        {/* いいね */}
        <section className="pt-6">
          <Favorit
            isFavorit={liked}
            analysisId={analysisId}
            contents={contents}
          />
        </section>

        {/* 対象作品 */}
        {contents.works.some((work) => work.length) && (
          <section className="pt-6">
            <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-GRAY_1">
              対象作品
            </p>
            <WorkListInDetail works={contents.works} />
          </section>
        )}

        {/* 本文（リッチテキスト表示） */}
        <section className="pt-16">
          <NovelPreview
            content={richTextContent}
            graphInfoList={graph}
          />
        </section>

        {/* アフィリエイトページリンク */}
        <AffiliateLink analysisId={analysisId} />
      </div>
    </section>
  );
}
